import { useQueries, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  SeasonApiRecord,
  SeasonApiStatus,
  SeasonsListQuery,
  SeasonsListResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import { unwrapGamePassCollection } from "./responseMappers";

/**
 * Fetcher for `GET /game-pass-admin/v2/admin/seasons`. Pure (no React) so
 * callers that need to drain every page from outside a component tree
 * (e.g. future xlsx export, schedule-picker preload) can call this
 * directly.
 *
 * Response envelope is non-standard for this BE service: `seasons` is
 * co-located with `success` at the top level rather than wrapped in
 * the usual `{ success, message, data }` shape. We forward the whole
 * envelope to React Query so consumers can read `response.seasons`
 * without having to know about the unwrapping difference.
 */
const fetchSeasonsList = async (
  params: SeasonsListQuery = {},
): Promise<SeasonsListResponse> => {
  const response = await getPrivateInstance().get<SeasonsListResponse>(
    API_ROUTES.GET_SEASONS_LIST,
    {
      params: {
        // Only forward `status` when explicitly provided so the canonical
        // listing URL stays clean (`/game-pass-admin/v2/admin/seasons` with no
        // query string) when the page is in its all-seasons mode.
        ...(params.status ? { status: params.status } : {}),
      },
    },
  );
  return {
    ...response.data,
    seasons: unwrapGamePassCollection<SeasonApiRecord>(response.data, "seasons"),
  };
};

/**
 * React Query hook bound to `GET /game-pass-admin/v2/admin/seasons` for a
 * single status (or unfiltered when `status` is omitted).
 *
 * The listing page no longer calls this directly — it uses the
 * plural `useGetSeasonsListByStatuses` so the Timeline tab can fan
 * out into two parallel `?status=active` + `?status=scheduled`
 * fetches. This single-status variant stays exported for callers
 * that genuinely need exactly one status (e.g. a Drafts-only picker
 * in the wizard) and as the building block the multi-status hook
 * internally composes.
 *
 * `staleTime` matches the rest of the admin panel (30s) — admins
 * editing a season expect the listing to reflect their change within
 * a few seconds, but we don't need to refetch on every focus.
 */
export const useGetSeasonsList = (
  params: SeasonsListQuery = {},
): UseQueryResult<SeasonsListResponse, Error> =>
  useQuery<SeasonsListResponse, Error>({
    queryKey: ["game-pass", "seasons", "list", params.status ?? ""],
    queryFn: () => fetchSeasonsList(params),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });

/** Aggregated result of the multi-status hook. Mirrors the parts of
 *  `UseQueryResult` the listing page consumes — kept narrow on
 *  purpose so consumers don't grow accidental dependencies on internal
 *  React Query state (`fetchStatus`, `dataUpdatedAt`, etc.). */
export type UseGetSeasonsListByStatusesResult = {
  /** Flattened list of every season record across every sub-query.
   *  Each sub-query's `seasons` array is concatenated in the order
   *  the statuses were requested. Duplicates would only occur if BE
   *  returns the same `season_id` under two different statuses (not
   *  possible given the lifecycle is mutually exclusive), so no
   *  dedupe step is needed. */
  data: ReadonlyArray<SeasonApiRecord>;
  /** True while ANY sub-query is in its first-load state. Cached
   *  sub-queries that resolve instantly don't keep this flag true —
   *  switching back to a previously-loaded tab feels instantaneous. */
  isLoading: boolean;
  /** True while ANY sub-query is fetching (incl. background refetches). */
  isFetching: boolean;
  /** True only when EVERY sub-query failed. A partial failure (one
   *  succeeded, one errored) still surfaces the successful slice
   *  through `data` so admins see what's available rather than a
   *  full-page error — matches the page's "fall through to empty
   *  cards on error" UX contract. */
  isError: boolean;
  /** Per-sub-query error in request order. `null` for sub-queries
   *  that succeeded. Exposed so future telemetry can attribute
   *  failures to specific statuses without re-issuing the request. */
  errors: ReadonlyArray<Error | null>;
};

/**
 * Multi-status seasons listing hook. Fires one parallel `GET
 * /game-pass-admin/v2/admin/seasons?status=<X>` request per entry in
 * `statuses`, aggregates the results, and exposes a single
 * `UseGetSeasonsList…` shape so the page doesn't have to juggle N
 * `UseQueryResult` objects.
 *
 * `statuses === undefined` collapses to a single unfiltered fetch —
 * matches the "All" tab's "give me everything, I'll partition
 * client-side" semantics. `statuses === []` is treated the same way
 * (defensive — no caller passes an empty array today, but the
 * fallback keeps the hook from rendering a permanently-empty state
 * if one ever does).
 *
 * Each sub-query has its own React Query key (`["game-pass",
 * "seasons", "list", "<status>"]`) so:
 *   1. Cache hits work per-status — switching from Drafts to
 *      Timeline reuses any previously-fetched Active / Scheduled
 *      slices without a new network call.
 *   2. The `staleTime: 30s` budget is per-status, so admins editing
 *      one bucket don't blow out the cache for the others.
 *   3. `retryOnTransientErrors` (skip 4xx, retry once on 5xx /
 *      network) applies independently to each sub-query, so a 404
 *      on one status doesn't cancel or delay the others.
 */
export const useGetSeasonsListByStatuses = (
  statuses?: ReadonlyArray<SeasonApiStatus>,
): UseGetSeasonsListByStatusesResult => {
  // Empty / undefined collapses to a single unfiltered query so the
  // "All" tab keeps working through the same hook surface. The
  // sentinel `undefined` flows through `fetchSeasonsList` which then
  // omits the `?status=` param.
  const queriesToFire: ReadonlyArray<SeasonApiStatus | undefined> =
    statuses && statuses.length > 0 ? statuses : [undefined];

  const results = useQueries({
    queries: queriesToFire.map((status) => ({
      queryKey: ["game-pass", "seasons", "list", status ?? ""],
      queryFn: () => fetchSeasonsList(status ? { status } : {}),
      placeholderData: (previousData: SeasonsListResponse | undefined) =>
        previousData,
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
      retry: retryOnTransientErrors,
    })),
  }) as ReadonlyArray<UseQueryResult<SeasonsListResponse, Error>>;

  // Flat-map keeps the request-order of `statuses` so the page's
  // partitioner sees a deterministic stream — useful for tests that
  // assert "active record appears before scheduled record" and for
  // any future debug logging that compares to the call order.
  const data = results.flatMap((result) => result.data?.seasons ?? []);
  const isLoading = results.some((result) => result.isLoading);
  const isFetching = results.some((result) => result.isFetching);
  // `every` here is deliberate — see the type doc on `isError` for
  // why partial failure is NOT surfaced as a global error.
  const isError =
    results.length > 0 && results.every((result) => result.isError);
  const errors = results.map((result) => result.error ?? null);

  return { data, isLoading, isFetching, isError, errors };
};

export { fetchSeasonsList };
